import { attr, belongsTo } from "@ember-data/model";
import dayjs from "dayjs";
import {
  validateDate,
  validateInclusion,
  validateNumber,
  validatePresence
} from "ember-changeset-validations/validators";
import type { IntlService } from "ember-intl";
import JournalEvent from "parog-web/models/journal-event";
import type Patient from "parog-web/models/patient";
import tWithDefault from "parog-web/utils/t-with-default";

//---- Automatically generated, don't update this part ----

type BloodInStoolFrequencyStringEnum = "1" | "2" | "3";

type AnalIncontinenceFrequencyStringEnum = "0" | "1" | "2" | "3";

type ImmediateStoolUrgencyStringEnum =
  | "0"
  | "1"
  | "2"
  | "3"
  | "4"
  | "5"
  | "6"
  | "7"
  | "8"
  | "9"
  | "10";

type WellBeingStringEnum = "0" | "1" | "2" | "3" | "4";

type ArticularPainSeverityStringEnum = "1" | "2" | "3" | "4";

/**
 * Represents a RCH survey filled.
 */
export default class RchSurveyFilledJournalEvent extends JournalEvent {
  static type = "rch-survey-filled-journal-event";

  @attr("number") declare averageStoolsNumber: number;

  @attr("boolean", {
    allowNull: true
  })
  declare bloodInStool: boolean;

  @attr("string")
  declare bloodInStoolFrequency: BloodInStoolFrequencyStringEnum;

  @attr("boolean", {
    allowNull: true
  })
  declare analIncontinence: boolean;

  @attr("string")
  declare analIncontinenceFrequency: AnalIncontinenceFrequencyStringEnum;

  @attr("boolean", {
    allowNull: true
  })
  declare immediateStool: boolean;

  @attr("string")
  declare immediateStoolUrgency: ImmediateStoolUrgencyStringEnum;

  @attr("string") declare wellBeing: WellBeingStringEnum;

  @attr("boolean", {
    allowNull: true
  })
  declare articularPain: boolean;

  @attr("string")
  declare articularPainSeverity: ArticularPainSeverityStringEnum;

  @attr("boolean", {
    allowNull: true
  })
  declare mouthUlcers: boolean;

  @attr("boolean", {
    allowNull: true
  })
  declare skinSymptoms: boolean;

  @attr("boolean", {
    allowNull: true
  })
  declare eyeSymptoms: boolean;

  @attr("boolean", {
    allowNull: true
  })
  declare eyeSymptomsRedEye: boolean;

  @attr("boolean", {
    allowNull: true
  })
  declare eyeSymptomsPainful: boolean;

  @attr("boolean", {
    allowNull: true
  })
  declare eyeSymptomsSight: boolean;

  @attr("boolean", {
    allowNull: true
  })
  declare fever: boolean;

  @attr("number") declare feverLevel: number;

  @attr("number") declare weight: number;

  @attr("number") declare score: number;

  static validations(intl: IntlService) {
    return {
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.rch-survey-filled-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date",
          onOrBefore: dayjs().endOf("day").toDate()
        })
      ],
      averageStoolsNumber: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.rch-survey-filled-journal-event.average-stools-number"
        )
      }),
      feverLevel: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.rch-survey-filled-journal-event.fever-level"
        )
      }),
      weight: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.rch-survey-filled-journal-event.weight"
        )
      }),
      score: validateNumber({
        allowBlank: true,
        description: intl.t("validations.rch-survey-filled-journal-event.score")
      }),
      bloodInStoolFrequency: validateInclusion({
        list: ["1", "2", "3"],
        allowBlank: true,
        description: intl.t(
          "validations.rch-survey-filled-journal-event.blood-in-stool-frequency"
        )
      }),
      analIncontinenceFrequency: validateInclusion({
        list: ["0", "1", "2", "3"],
        allowBlank: true,
        description: intl.t(
          "validations.rch-survey-filled-journal-event.anal-incontinence-frequency"
        )
      }),
      immediateStoolUrgency: validateInclusion({
        list: ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"],
        allowBlank: true,
        description: intl.t(
          "validations.rch-survey-filled-journal-event.immediate-stool-urgency"
        )
      }),
      wellBeing: validateInclusion({
        list: ["0", "1", "2", "3", "4"],
        allowBlank: true,
        description: intl.t(
          "validations.rch-survey-filled-journal-event.well-being"
        )
      }),
      articularPainSeverity: validateInclusion({
        list: ["1", "2", "3", "4"],
        allowBlank: true,
        description: intl.t(
          "validations.rch-survey-filled-journal-event.articular-pain-severity"
        )
      })
    };
  }

  //---- End of generated code ----

  static sections = [
    "averageStoolsNumber",
    "bloodInStool",
    "bloodInStoolFrequency",
    "analIncontinence",
    "analIncontinenceFrequency",
    "immediateStool",
    "immediateStoolUrgency",
    "wellBeing",
    "articularPain",
    "articularPainSeverity",
    "mouthUlcers",
    "skinSymptoms",
    "eyeSymptoms",
    "eyeSymptomsRedEye",
    "eyeSymptomsPainful",
    "eyeSymptomsSight",
    "fever",
    "feverLevel",
    "weight"
  ];

  @belongsTo("patient", {
    async: false,
    inverse: "rchSurveyFilledJournalEvents"
  })
  declare patient: Patient;
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "rch-survey-filled-journal-event": RchSurveyFilledJournalEvent;
  }
}
